<script lang="ts" setup>
import { onBeforeMount, ref } from 'vue';
import { confirmAccess, useUserStore } from './services/auth';
import { auth, provider } from './firebase-config';
import { signInWithPopup } from 'firebase/auth';

// --- Authentication State ---
const userStore = useUserStore();
const userChecked = ref(false);
const authenticated = ref(false);
const adminClaim = ref(false);
const serverAdminClaim = ref(false);

// --- Login State ---
const loginError = ref('');
const isLoggingIn = ref(false);

// --- Subscribe to User Store Changes ---
userStore.$subscribe(
	async (_, state) => {
		if (state.user) {
			authenticated.value = true;
		} else {
			authenticated.value = false;
		}

		if (state.admin) {
			adminClaim.value = true;
		} else {
			adminClaim.value = false;
		}

		if (state.serverAdmin) {
			serverAdminClaim.value = true;
		} else {
			serverAdminClaim.value = false;
		}
	},
	{ flush: 'sync' }
);

// --- Methods ---
const attemptGoogleLogin = async () => {
	isLoggingIn.value = true;
	loginError.value = '';
	signInWithPopup(auth, provider)
		.then(async (result) => {
			await confirmAccess(auth); // check acumapify access and if user is an admin
			userStore.$patch({ user: result.user }); // update user store with user and admin claims if found
		})
		.catch((e) => {
			if (e.message.includes('popup-closed-by-user')) {
				loginError.value = 'NO ACCOUNT SELECTED';
			} else if (
				e.message.includes('admin-restricted-operation') ||
				e.message.includes('user-disabled') ||
				e.message.includes('unauthorized-email')
			) {
				loginError.value = 'UNRECOGNIZED ACCOUNT';
			} else {
				loginError.value = 'AN UNKNOWN ERROR OCURRED';
			}
		})
		.finally(() => {
			isLoggingIn.value = false;
		});
};

// --- Lifecycle Hooks ---
const awaitAuth = async () => {
	await auth.authStateReady(); // ensure firebase auth checks have been completed
	await confirmAccess(auth); // check access to acumapify and if user is admin
	userChecked.value = true; // allow page visibility
};

onBeforeMount(async () => {
	await awaitAuth();
});
</script>

<template>
	<!-- App -->
	<div v-if="userChecked" class="max-space">
		<!-- Main Content (authenticated) -->
		<div v-if="authenticated" class="max-space-flex">
			<nav class="background-secondary">
				<router-link to="/">
					<span class="material-symbols-outlined">home</span>
					<p>Home</p>
				</router-link>
				<router-link v-if="adminClaim" to="/inventory">
					<span class="material-symbols-outlined">package_2</span>
					<p>Inventory</p>
				</router-link>
				<router-link to="/webquotes">
					<span class="material-symbols-outlined">price_check</span>
					<p>Web Quotes</p>
				</router-link>
				<router-link to="/weborders">
					<span class="material-symbols-outlined">shopping_cart</span>
					<p>Web Orders</p>
				</router-link>
				<router-link v-if="serverAdminClaim" to="/users">
					<span class="material-symbols-outlined">group</span>
					<p>Users</p>
				</router-link>
			</nav>
			<div class="max-space">
				<router-view />
			</div>
		</div>

		<!-- Login Page (unauthenticated) -->
		<div v-else class="max-view-flex page-padding">
			<!-- Mintech's Logo and Slogan -->
			<div class="login-view__left">
				<img src="../src/assets/logos/www-white-logo.png" alt="www-white-logo.png" style="width: 125px; height: 125px" />
				<div class="slogan">
					<h1 class="company-name">MINTECH</h1>
					<h2 class="company-slogan">Wear What Works.</h2>
				</div>
			</div>

			<!-- Login Panel -->
			<div class="login-view__right">
				<img src="../src/assets/logos/acumapify-logo.svg" alt="logo" style="width: 100px; height: 100px" />
				<p class="fira semi-large">Welcome to Acumapify!</p>
				<button type="button" class="login-with-google-btn" @click="attemptGoogleLogin" :disabled="isLoggingIn">Sign in with Google</button>
				<p class="poppins tiny bold red" v-if="loginError">{{ loginError }}</p>
			</div>
		</div>
	</div>
</template>

<style>
#app {
	font-family: Avenir, Helvetica, Arial, sans-serif;
	-webkit-font-smoothing: antialiased;
	-moz-osx-font-smoothing: grayscale;

	display: flex;
	height: 100vh;
	width: 100vw;
	background-color: #212529;
	color: #2c3e50;
}

nav {
	flex: none;
	width: 125px;
	height: 100vh;
	display: flex;
	flex-direction: column;
	gap: 15px;
	padding: 20px;
	box-sizing: border-box;
}

nav a {
	text-decoration: none;
	text-align: center;

	display: flex;
	flex-direction: column;
	gap: 10px;
	padding: 15px 0;
	border-radius: 5px;

	.material-symbols-outlined {
		color: #ffe8f0;
		font-weight: 200;
		margin: 0;
	}

	p {
		color: #ffe8f0;
		font-family: 'Fira Sans';
		font-weight: 200;
		margin: 0;
	}
}

nav a.router-link-exact-active,
nav a:hover {
	background-color: #3d4248;

	.material-symbols-outlined {
		color: white;
		font-weight: 300;
	}

	p {
		color: white;
		font-weight: 400;
	}
}

/* Login */
.login-view__left {
	display: flex;
	flex: 1 1 auto;
	align-items: center;
	user-select: none;
}

.slogan {
	display: flex;
	flex-direction: column;
	align-items: flex-start;

	h1,
	h2 {
		color: #eeeeee;
		margin: 0;
	}

	.company-name {
		font-family: 'Raleway';
		font-weight: bold;
		letter-spacing: 2.6px;
		font-size: 0.75rem;
	}

	.company-slogan {
		font-family: 'Poppins';
		font-size: 2.5rem;
		font-weight: normal;
	}
}

.login-view__right {
	width: 450px;
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 20px;
	border-radius: 15px;
	background: #eeeeee;
}

input {
	border: 1px solid rgba(0, 0, 0, 0.25);
	font-family: 'Poppins';
	border-radius: 5px;
	padding: 5px 10px;
	width: 90%;
	box-sizing: border-box;
}

.login-with-google-btn {
	transition: background-color 0.3s, box-shadow 0.3s;

	padding: 12px 16px 12px 42px;
	border: none;
	border-radius: 3px;
	box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 1px 1px rgba(0, 0, 0, 0.25);

	color: #757575;
	font-size: 14px;
	font-weight: 500;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
		sans-serif;

	background-image: url(data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTgiIGhlaWdodD0iMTgiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGcgZmlsbD0ibm9uZSIgZmlsbC1ydWxlPSJldmVub2RkIj48cGF0aCBkPSJNMTcuNiA5LjJsLS4xLTEuOEg5djMuNGg0LjhDMTMuNiAxMiAxMyAxMyAxMiAxMy42djIuMmgzYTguOCA4LjggMCAwIDAgMi42LTYuNnoiIGZpbGw9IiM0Mjg1RjQiIGZpbGwtcnVsZT0ibm9uemVybyIvPjxwYXRoIGQ9Ik05IDE4YzIuNCAwIDQuNS0uOCA2LTIuMmwtMy0yLjJhNS40IDUuNCAwIDAgMS04LTIuOUgxVjEzYTkgOSAwIDAgMCA4IDV6IiBmaWxsPSIjMzRBODUzIiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNNCAxMC43YTUuNCA1LjQgMCAwIDEgMC0zLjRWNUgxYTkgOSAwIDAgMCAwIDhsMy0yLjN6IiBmaWxsPSIjRkJCQzA1IiBmaWxsLXJ1bGU9Im5vbnplcm8iLz48cGF0aCBkPSJNOSAzLjZjMS4zIDAgMi41LjQgMy40IDEuM0wxNSAyLjNBOSA5IDAgMCAwIDEgNWwzIDIuNGE1LjQgNS40IDAgMCAxIDUtMy43eiIgZmlsbD0iI0VBNDMzNSIgZmlsbC1ydWxlPSJub256ZXJvIi8+PHBhdGggZD0iTTAgMGgxOHYxOEgweiIvPjwvZz48L3N2Zz4=);
	background-color: white;
	background-repeat: no-repeat;
	background-position: 12px 11px;

	&:hover {
		box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.25);
	}

	&:active {
		background-color: #eeeeee;
	}

	&:focus {
		outline: none;
		box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 2px 4px rgba(0, 0, 0, 0.25), 0 0 0 3px #c8dafc;
	}

	&:disabled {
		filter: grayscale(100%);
		background-color: #ebebeb;
		box-shadow: 0 -1px 0 rgba(0, 0, 0, 0.04), 0 1px 1px rgba(0, 0, 0, 0.25);
		cursor: not-allowed;
	}
}
</style>
